import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const EMPTY_FORM = {
  name: "", city: "", country: "", stars: "1",
  latitude: "", longitude: "", cuisine_type: "",
  price_range: "", reservation_url: ""
};

const AddRestaurantTab = ({ onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim() || !form.country.trim()) {
      toast.error("Bitte Name, Stadt und Land ausfüllen");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        stars: parseInt(form.stars, 10),
        latitude: form.latitude !== "" ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== "" ? parseFloat(form.longitude) : null,
        cuisine_type: form.cuisine_type.trim() || null,
        price_range: form.price_range || null,
        reservation_url: form.reservation_url.trim() || null
      };
      const res = await axios.post(`${API}/admin/restaurants`, payload, { withCredentials: true });
      toast.success(`„${res.data.restaurant.name}" wurde hinzugefügt!`);
      setForm(EMPTY_FORM);
      if (onCreated) onCreated();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Restaurant konnte nicht hinzugefügt werden");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-card border-border max-w-3xl" data-testid="add-restaurant-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5 text-primary" /> Restaurant hinzufügen
        </CardTitle>
        <CardDescription>
          Fehlende Michelin-Restaurants manuell zur Datenbank hinzufügen. Mit Koordinaten erscheint das Restaurant auch auf der Karte.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="rest-name">Name *</Label>
              <Input id="rest-name" data-testid="add-restaurant-name-input" value={form.name} onChange={set("name")} placeholder="z.B. 42 Restaurant" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-city">Stadt *</Label>
              <Input id="rest-city" data-testid="add-restaurant-city-input" value={form.city} onChange={set("city")} placeholder="z.B. Wolfsburg" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-country">Land *</Label>
              <Input id="rest-country" data-testid="add-restaurant-country-input" value={form.country} onChange={set("country")} placeholder="z.B. Germany" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label>Michelin-Sterne *</Label>
              <Select value={form.stars} onValueChange={(v) => setForm({ ...form, stars: v })}>
                <SelectTrigger data-testid="add-restaurant-stars-select" className="bg-transparent border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1"><span className="flex items-center gap-1"><Star className="w-3 h-3 fill-primary text-primary" /> 1 Stern</span></SelectItem>
                  <SelectItem value="2"><span className="flex items-center gap-1"><Star className="w-3 h-3 fill-primary text-primary" /><Star className="w-3 h-3 fill-primary text-primary" /> 2 Sterne</span></SelectItem>
                  <SelectItem value="3"><span className="flex items-center gap-1"><Star className="w-3 h-3 fill-primary text-primary" /><Star className="w-3 h-3 fill-primary text-primary" /><Star className="w-3 h-3 fill-primary text-primary" /> 3 Sterne</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-cuisine">Küche</Label>
              <Input id="rest-cuisine" data-testid="add-restaurant-cuisine-input" value={form.cuisine_type} onChange={set("cuisine_type")} placeholder="z.B. Modern European" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-lat">Breitengrad (Latitude)</Label>
              <Input id="rest-lat" data-testid="add-restaurant-latitude-input" type="number" step="any" value={form.latitude} onChange={set("latitude")} placeholder="z.B. 52.4227" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-lng">Längengrad (Longitude)</Label>
              <Input id="rest-lng" data-testid="add-restaurant-longitude-input" type="number" step="any" value={form.longitude} onChange={set("longitude")} placeholder="z.B. 10.7865" className="bg-transparent border-border" />
            </div>
            <div className="space-y-2">
              <Label>Preisklasse</Label>
              <Select value={form.price_range} onValueChange={(v) => setForm({ ...form, price_range: v })}>
                <SelectTrigger data-testid="add-restaurant-price-select" className="bg-transparent border-border">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="€€">€€</SelectItem>
                  <SelectItem value="€€€">€€€</SelectItem>
                  <SelectItem value="€€€€">€€€€</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rest-url">Reservierungs-Link</Label>
              <Input id="rest-url" data-testid="add-restaurant-url-input" value={form.reservation_url} onChange={set("reservation_url")} placeholder="https://..." className="bg-transparent border-border" />
            </div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="add-restaurant-submit-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
            {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Restaurant hinzufügen
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddRestaurantTab;
